import type { TFunction } from 'i18next'

import type { VatDeclarationsReport } from '@features/vatDeclarations/types/vatDeclarationsReport'
import type { VatDeclarationsReportField } from '@features/vatDeclarations/types/vatDeclarationsReportField'

import { shouldUseDKReportContent } from '../../../../../../../../utils/isSupportedCountryId'
import { getAmountColumnConfig } from '../../../../utils/getAmountColumnConfig'
import { PlainRow } from '../types/plainRow'

interface BuildFlatReportTableProps {
  data?: VatDeclarationsReport
  countryId: string
  t: TFunction
}

const formatReport = (data: VatDeclarationsReport, countryId: string, t: TFunction): PlainRow[] => {
  const isDK = shouldUseDKReportContent(countryId)
  const rows: PlainRow[] = []

  const pushSection = (name: string | null, fields: VatDeclarationsReportField[]) => {
    if (fields.length === 0) {
      return
    }
    const { labels, amountKeys } = getAmountColumnConfig(countryId, fields, t)
    rows.push({ kind: 'section', name: name ?? '' })
    if (labels.length > 0) {
      rows.push({ kind: 'header', labels })
    }
    for (const field of fields) {
      const amounts = isDK ? [field.vatAmount ?? field.turnoverAmount ?? 0] : amountKeys.map((key) => field[key] ?? 0)
      rows.push({ kind: 'field', symbol: isDK ? null : field.symbol, name: field.name, amounts })
    }
    rows.push({ kind: 'spacer' })
  }

  for (const item of data) {
    if ('sections' in item) {
      if (item.name) {
        rows.push({ kind: 'section', name: item.name })
      }
      for (const section of item.sections) {
        pushSection(section.name, section.fields)
      }
    } else {
      pushSection(item.name, item.fields ?? [])
    }
  }

  return rows
}

const toArrayRow = (row: PlainRow) => {
  switch (row.kind) {
    case 'header':
      return ['', '', ...row.labels]
    case 'section':
      return [row.name]
    case 'field':
      return row.symbol !== null ? [row.symbol, row.name, ...row.amounts] : [row.name, ...row.amounts]
    case 'spacer':
    default:
      return []
  }
}

export const buildFlatReportTable = ({ data, countryId, t }: BuildFlatReportTableProps) => {
  if (!data || data.length === 0) {
    return []
  }
  return formatReport(data, countryId, t).map(toArrayRow)
}
